/**
 * Authored icons on a 24px grid, 1.5px stroke, inheriting currentColor.
 * One consistent stroke and weight throughout — no emoji, no glyph
 * substitutes, no second icon style creeping in from a library.
 */

interface IconProps {
  size?: number;
  className?: string;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false as const,
});

export function HeronMark({ size = 22, className }: IconProps) {
  /**
   * A heron's head, beak and neck. Deliberately solid rather than drawn in the
   * 1.5px stroke of the icons below: this is a logo, it renders at 22-24px next
   * to bold text, and the earlier full-bird line drawing — body, legs and all —
   * dissolved into a squiggle at that size.
   *
   * Geometry is shared with public/favicon.svg so the tab and the console show
   * the same bird. Keep the two in step; the only difference is that the
   * favicon sits on an accent tile and this inherits currentColor.
   *
   * The transform scales the mark off the favicon's tile padding to fill the
   * box, and takes the stroke weight with it — so proportions stay identical.
   */
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="currentColor"
      className={className}
      aria-hidden
      focusable={false}
    >
      <g transform="translate(16 16) scale(1.45) translate(-15.8 -16.75)">
        <path
          d="M17.8 24.6 C 16.6 19.4, 12.2 18.2, 12.4 13.2"
          fill="none"
          stroke="currentColor"
          strokeWidth={4}
          strokeLinecap="round"
        />
        {/* Head and eye as one evenodd path, so the eye is a hole rather than
            a second colour — the mark inherits currentColor and sits on panel,
            card and dark backgrounds, none of which it can hardcode. Without
            the eye the head fuses into the neck and the bird reads as a hook. */}
        <path
          fillRule="evenodd"
          d="M9.4 11.4a3 3 0 1 1 6 0 3 3 0 1 1-6 0Z
             M10.85 10.8a0.95 0.95 0 1 0 1.9 0 0.95 0.95 0 1 0-1.9 0Z"
        />
        <path d="M14.5 9.9 L22.2 6.9 L15 12.7 Z" />
      </g>
    </svg>
  );
}

export const IconDashboard = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M4 13h4v7H4zM10 8h4v12h-4zM16 4h4v16h-4z" />
  </svg>
);

export const IconCompanies = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M3 21h18M5 21V6a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v15M14 21V10h4a1 1 0 0 1 1 1v10" />
    <path d="M8 9h3M8 13h3M8 17h3" />
  </svg>
);

export const IconLicense = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 10h18M7 14.5h5" />
  </svg>
);

export const IconProducts = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M12 3 3 7.5 12 12l9-4.5L12 3Z" />
    <path d="M3 12.5 12 17l9-4.5M3 17 12 21.5 21 17" />
  </svg>
);

export const IconAudit = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M8 4h9a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7" />
    <path d="M5 7V4h3M9 11h6M9 15h6" />
  </svg>
);

export const IconMail = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </svg>
);

export const IconPlus = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconSearch = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4 4" />
  </svg>
);

export const IconClose = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export const IconCheck = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="m4 12.5 5 5L20 6.5" />
  </svg>
);

export const IconAlert = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M12 4.5 2.8 20h18.4L12 4.5Z" />
    <path d="M12 10v4.5M12 17.2v.3" />
  </svg>
);

export const IconInfo = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5M12 8.2v.3" />
  </svg>
);

export const IconCopy = ({ size = 15, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M15 6.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h.5" />
  </svg>
);

export const IconArrowLeft = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M19 12H5m0 0 6-6m-6 6 6 6" />
  </svg>
);

export const IconChevronRight = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="m9 5 7 7-7 7" />
  </svg>
);

export const IconKey = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="8" cy="8" r="4.5" />
    <path d="m11.5 11.5 8 8M17 17l-2 2M19.5 14.5l-2 2" />
  </svg>
);

export const IconUser = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="12" cy="8" r="3.75" />
    <path d="M4.5 20c0-3.6 3.4-5.75 7.5-5.75S19.5 16.4 19.5 20" />
  </svg>
);

export const IconShield = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M12 3 5 5.6v5.6c0 4.2 2.9 7.7 7 9.2 4.1-1.5 7-5 7-9.2V5.6L12 3Z" />
    <path d="m9 12 2.2 2.2L15.2 10" />
  </svg>
);

export const IconClock = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

export const IconRefresh = ({ size = 15, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M20 12a8 8 0 1 1-2.5-5.8M20 4v4h-4" />
  </svg>
);

export const IconSettings = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

export const IconSun = ({ size = 17, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
  </svg>
);

export const IconMoon = ({ size = 17, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </svg>
);

export const IconInbox = ({ size = 32, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M3 13h4l1.5 3h7L17 13h4" />
    <path d="M5.5 5h13l2.5 8v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5l2.5-8Z" />
  </svg>
);

export const IconLogout = ({ size = 16, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M14 5H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7" />
    <path d="M17 9l3 3-3 3M20 12h-8" />
  </svg>
);

export const IconSync = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <ellipse cx="7" cy="6" rx="4" ry="2" />
    <path d="M3 6v5c0 1.1 1.8 2 4 2s4-.9 4-2V6" />
    <path d="M13 16h7M17 13l3 3-3 3" />
    <path d="M3 11v5c0 1.1 1.8 2 4 2h2" />
  </svg>
);
